import Spots from './components/Spots/Spots';
import SpotGenerator from './components/Spots/SpotGenerator';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';

import Main from './pages/main/Main';
import Privacy from './pages/privacy/Privacy';
import Client from './pages/client/Client';
import Services from './pages/services/Services';
import Partners from './pages/partners/Partners';
import Admin from './pages/admin/Admin';
import Product from './pages/product/Product';
import CalculatorPage from './pages/product/CalculatorPage';
import ProductDetailPage from './pages/product/ProductDetailPage';

import PrivacyNotice from './components/Cookie/CookieBanner';
import MetrikaRouteTracker from './components/Cookie/MetrikaRouteTracker'
import Navbar from './components/Navbar/Navbar';
import VpnGuard from './components/VpnGuard/VpnGuard';
import SeoMeta from './components/Seo/SeoMeta';
import StructuredData from './components/Seo/StructuredData';

import './App.css'
import './design-system.css'

function isPublicProduct(product) {
    return !product?.hidden;
}

const listPage = [
    { path: '/', label: 'О нас', href: '/', element: Main },
    { path: '/services', label: 'Услуги', href: '/services', element: Services },
    { path: '/client', label: 'Клиентам', href: '/client', element: Client },
    { path: '/partners', label: 'Партнерам', href: '/partners', element: Partners },
    { path: '/product', label: 'Продукты', href: '/product', element: Product },
    { path: '/privacy', href: '/privacy', element: Privacy },
    { path: '/admin', href: '/admin', element: Admin }
];

function App() {
    const location = useLocation();
    const appContainerRef = useRef(null);
    const lastTallPageRef = useRef(null);
    const [spotsLeft, setSpotsLeft] = useState([]);
    const [spotsRight, setSpotsRight] = useState([]);
    const [spotsBottomRight, setSpotsbottomRight] = useState([]);
    const [spotsBottomLeft, setSpotsbottomLeft] = useState([]);
    const [hasProducts, setHasProducts] = useState(false);
    const [topSpotOffset] = useState(() => window.innerWidth > 950 ? 300 : -300);
    const [bottomSpotOffset] = useState(() => window.innerWidth > 950 ? 150 : -150);

    const [heightCheck, setHeightCheck] = useState(false);

    const heightEdge = 2300

    useEffect(() => {
        let isActive = true;

        async function checkProducts() {
            try {
                const response = await axios.get("/api/products");
                if (isActive) {
                    setHasProducts((response.data.data ?? []).some(isPublicProduct));
                }
            } catch (error) {
                if (isActive) {
                    setHasProducts(false);
                }
            }
        }

        checkProducts();

        return () => {
            isActive = false;
        };
    }, []);

    useEffect(() => {
        let isActive = true;

        const loadSpots = async () => {
            const container = appContainerRef.current;
            if (!container) return;

            const pageHeight = container.scrollHeight;
            const isTallPage = pageHeight > heightEdge;

            setHeightCheck(isTallPage);
            lastTallPageRef.current = isTallPage;

            const requests = [
                SpotGenerator.generate("spot1"),
                SpotGenerator.generate("spot2"),
            ];

            if (isTallPage) {
                requests.push(
                    SpotGenerator.generate("spot3"),
                    SpotGenerator.generate("spot4")
                );
            }

            const [left, right, bottomRight = [], bottomLeft = []] = await Promise.all(requests);

            if (!isActive) return;

            setSpotsLeft(left);
            setSpotsRight(right);
            setSpotsbottomRight(bottomRight);
            setSpotsbottomLeft(bottomLeft);
        };

        const frameId = window.requestAnimationFrame(() => {
            loadSpots();
        });

        return () => {
            isActive = false;
            window.cancelAnimationFrame(frameId);
        };
    }, [location.pathname]);

    useEffect(() => {
        const container = appContainerRef.current;
        if (!container) return undefined;

        const observer = new ResizeObserver(() => {
            const isTallPage = container.scrollHeight > heightEdge;

            setHeightCheck(isTallPage);

            if (lastTallPageRef.current === isTallPage) return;

            lastTallPageRef.current = isTallPage;

            const requests = [
                SpotGenerator.generate("spot1"),
                SpotGenerator.generate("spot2"),
            ];

            if (isTallPage) {
                requests.push(
                    SpotGenerator.generate("spot3"),
                    SpotGenerator.generate("spot4")
                );
            }

            Promise.all(requests).then(([left, right, bottomRight = [], bottomLeft = []]) => {
                setSpotsLeft(left);
                setSpotsRight(right);
                setSpotsbottomRight(bottomRight);
                setSpotsbottomLeft(bottomLeft);
            });
        });

        observer.observe(container);

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const container = appContainerRef.current;
        if (!container) return undefined;

        const elements = Array.from(container.querySelectorAll([
            'main:not(.admin) h1',
            'main:not(.admin) h2',
            'main:not(.admin) h3',
            'main:not(.admin) p',
            'main:not(.admin) .card',
            'main:not(.admin) .cardHorizontal',
            'main:not(.admin) .product__card',
            'main:not(.admin) .productDetail__featureCard',
            'main:not(.admin) .productDetail__useCaseCard',
            'main:not(.admin) .productDetail__content',
            'main:not(.admin) .productDetail__finalCta',
        ].join(','))).filter((element) => !element.closest('.footer__container'));

        elements.forEach((element, index) => {
            element.classList.add('reveal-on-scroll');
            element.style.setProperty('--reveal-delay', `${Math.min(index % 6, 5) * 55}ms`);
        });

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;

                entry.target.classList.add('is-visible');
                observer.unobserve(entry.target);
            });
        }, {
            rootMargin: '0px 0px -12% 0px',
            threshold: 0.12,
        });

        elements.forEach((element) => observer.observe(element));

        return () => observer.disconnect();
    }, [location.pathname, hasProducts]);

    return (
        <div className="app-container" ref={appContainerRef}>
            <div className="spot-layer" aria-hidden="true">

                <div className="spot-overlay-left" style={{ height: heightCheck ? '50%' : '100%' }}>
                    <Spots spots={spotsLeft} top={60} left={topSpotOffset} />
                </div>
                <div className="spot-overlay-right" style={{ height: heightCheck ? '50%' : '100%' }}>
                    <Spots spots={spotsRight} top={540} right={600} spotDelay={.7} />
                </div>

                {heightCheck && (
                    <>
                        <div className="spot-overlay-bottomRight">
                            <Spots spots={spotsBottomRight} bottom={520} right={600} />
                        </div>
                        <div className="spot-overlay-bottomLeft">
                            <Spots spots={spotsBottomLeft} bottom={700} left={bottomSpotOffset} />
                        </div>
                    </>
                )}

            </div>

            <Navbar navItems={listPage.filter(page => page.label !== "" && (page.path !== "/product" || hasProducts))} />
            <PrivacyNotice />
            <VpnGuard />
            <MetrikaRouteTracker />
            <SeoMeta />
            <StructuredData />

            <Routes>
                {listPage.map((page) => (
                    <Route key={page.path} path={page.path} element={<page.element hasProducts={hasProducts} />} />
                ))}
                <Route path="/admin/requests" element={<Admin view="requests" />} />
                <Route path="/product/calculator" element={<CalculatorPage />} />
                <Route path="/product/:productId" element={<ProductDetailPage />} />
            </Routes>
        </div>
    );
}

export default App;
